import { AppTypes, UserProperties, UserPropertiesKeys } from '@griply/types';

// Stored where it dies with the install, so a reinstall starts over (Firebase `first_open` semantics).
export type FirstOpenRecord = {
  at: string;
  is_logged_in: boolean;
};

export type FirstOpenStorage = {
  read: () => FirstOpenRecord | null;
  write: (record: FirstOpenRecord) => void;
};

export type FirstOpenUserProperties = Pick<
  UserProperties,
  UserPropertiesKeys.FirstOpenAt | UserPropertiesKeys.FirstOpenAppType
>;

export const parseFirstOpenRecord = (
  raw: string | null,
): FirstOpenRecord | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as FirstOpenRecord).at !== 'string' ||
      typeof (parsed as FirstOpenRecord).is_logged_in !== 'boolean'
    ) {
      return null;
    }

    return {
      at: (parsed as FirstOpenRecord).at,
      is_logged_in: (parsed as FirstOpenRecord).is_logged_in,
    };
  } catch {
    return null;
  }
};

export const recordFirstOpen = (
  storage: FirstOpenStorage,
  isLoggedIn: boolean,
  now: Date = new Date(),
): { record: FirstOpenRecord; isNew: boolean } => {
  const stored = storage.read();
  if (stored) {
    return { record: stored, isNew: false };
  }

  const record: FirstOpenRecord = {
    at: now.toISOString(),
    is_logged_in: isLoggedIn,
  };
  storage.write(record);

  return { record, isNew: true };
};

// A logged-in first open is an existing account on a new install (or the upgrade) — its real first open is elsewhere.
export const getFirstOpenUserProperties = (
  record: FirstOpenRecord | null,
  appType: AppTypes,
): FirstOpenUserProperties | null => {
  if (!record || record.is_logged_in) {
    return null;
  }

  return {
    [UserPropertiesKeys.FirstOpenAt]: record.at,
    [UserPropertiesKeys.FirstOpenAppType]: appType,
  };
};
